import { useReducer, useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { isNotNil, isNilOrEmpty } from '../../../../helpers/utils';
import { computeDateRange } from '../../../../helpers/date';

const states = {
  INIT: 'INIT',
  FETCH_FARES: 'FETCH_FARES',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  NO_RESULTS: 'NO_RESULTS',
  PROMPT_INVALID: 'PROMPT_INVALID'
};

const transitions = {
  [states.INIT]: {
    loading: states.LOADING
  },
  [states.FETCH_FARES]: {
    // loading: states.LOADING,
    fetch_fares: states.FETCH_FARES,
    success: states.COMPLETE,
    show_no_results: states.NO_RESULTS,
    error: states.ERROR
  },
  [states.LOADING]: {
    fetch_fares: states.FETCH_FARES,
    prompt_invalid: states.PROMPT_INVALID,
    error: states.ERROR
  },
  [states.NO_RESULTS]: {
    loading: states.LOADING
  },
  [states.ERROR]: {
    loading: states.LOADING
  },
  [states.COMPLETE]: {
    loading: states.LOADING,
    fetch_fares: states.FETCH_FARES
  },
  [states.PROMPT_INVALID]: {
    loading: states.LOADING,
    success: states.COMPLETE
  }
};

function reducer(prev, action) {
  const nextState = transitions[prev.value][action.transition];
  if (nextState) {
    return {
      ...prev,
      value: nextState,
      history: prev.value,
      ...action.payload
    };
  }
  return prev;
}

function useQuery({ getData, fetchData }) {
  useEffect(() => {
    if (!fetchData) {
      return;
    }
    getData();
  }, [fetchData, getData]);
}

const mapFilter = ({
  prompt,
  dateRange,
  sorting,
  prePopulation,
  locations,
  paginationIndex: currentPaginationIndex = undefined
}) => {
  const filterModified = { ...prePopulation };

  let sortingFilter = filterModified.sorting;

  if (isNotNil(sorting) && Array.isArray(sorting)) {
    if (sorting.some((sort) => isNotNil(sort.popularity))) {
      sortingFilter = sorting.filter((sort) => !isNotNil(sort.popularity));
    } else {
      sortingFilter = sorting;
    }
  } else if (sorting && typeof sorting === 'string') {
    switch (sorting) {
      case 'departure_date':
        sortingFilter = [
          {
            departureDate: 'ASC'
          }
        ];
        break;
      case 'most_popular':
        sortingFilter = [{ popularity: 'DESC' }];
        break;
      case 'price_specification_low_to_high':
        sortingFilter = [{ 'priceSpecification.usdTotalPrice': 'ASC' }];
        break;
      case 'price_specification_high_to_low':
        sortingFilter = [{ 'priceSpecification.usdTotalPrice': 'DESC' }];
        break;
      case 'duration_high_to_low':
        sortingFilter = [{ flightDeltaDays: 'DESC' }];
        break;
      case 'duration_low_to_high':
        sortingFilter = [{ flightDeltaDays: 'ASC' }];
        break;
      case 'no_sorting':
        sortingFilter = [];
        break;
      default:
        break;
    }
  }

  filterModified.fareSorting = sortingFilter?.filter((sort) => !isNotNil(sort.popularity));
  filterModified.routeSorting = sortingFilter?.filter((sort) => isNotNil(sort.popularity));
  delete filterModified.sorting;

  const { origin, destination } = locations;

  //INFO: Origins and destinations from the LLM are now filtered based on those coming from the airline
  if (Array.isArray(filterModified.origins) && filterModified.origins.length > 0) {
    filterModified.origins = filterModified.origins
      .map((originIataCode) => origin.data.find((item) => item.iataCode === originIataCode))
      .filter(Boolean)
      .map((item) => item.iataCode);
  } else if (prePopulation.origin) {
    filterModified.origins = [prePopulation.origin];
  }

  if (Array.isArray(filterModified.destinations) && filterModified.destinations.length > 0) {
    filterModified.destinations = filterModified.destinations
      .map((destinationIataCode) =>
        destination.data.find((item) => item.iataCode === destinationIataCode)
      )
      .filter(Boolean)
      .map((item) => item.iataCode);
  } else if (prePopulation.destination) {
    filterModified.destinations = [prePopulation.destination];
  }

  // Delete origin and destination from prePopulation
  delete filterModified?.origin;
  delete filterModified?.destination;
  delete filterModified.dateRange;
  if (!filterModified.departure && Object.keys(dateRange).length !== 0) {
    filterModified.departure = dateRange;
  }

  filterModified.prompt = prompt;

  if (isNotNil(currentPaginationIndex)) {
    filterModified.faresLimit = currentPaginationIndex;
  }

  delete filterModified.explanation;
  delete filterModified.prompt;
  delete filterModified.destinationDescription; //TODO: This in unnecessary param from LLM response
  delete filterModified.language; //TODO: This in unnecessary param from LLM response
  delete filterModified.airlineIataCode;

  sputnikArports = filterModified?.destinations;
  return filterModified;
};

function getResultsLength(results) {
  if (isNilOrEmpty(results)) {
    return 0;
  }
  return results.length;
}

function transformData(rawData, dataPrompt) {
  const groupedByRoute = rawData.reduce((acc, fare) => {
    const routeKey = `${fare.origin}-${fare.destination}`;
    if (!acc[routeKey]) {
      acc[routeKey] = [];
    }
    //Info: Adding tags
    const tags = [];
    if (fare?.isLowest) {
      tags.push('cheapest');
    }
    if (!fare?.flightStopCount) {
      tags.push('non-stop');
    }

    fare.tags = tags;

    if (!acc[routeKey].fares) {
      acc[routeKey].fares = [];
    }
    if (!acc[routeKey].info) {
      acc[routeKey].info = {
        route: {
          origin: fare.origin,
          originCity: fare.originCity,
          destination: fare.destination,
          destinationCity: fare.destinationCity,
          destinationImage: fare.destinationImage
        },
        ...dataPrompt.summary[fare.destination]
      };
    }
    acc[routeKey].fares.push(fare);
    return acc;
  }, {});

  const groupedTransformed = [];
  Object.keys(groupedByRoute).forEach((item) => {
    groupedTransformed.push({ ...groupedByRoute[item], route: item });
  });

  return groupedTransformed;
}

/**
 * This object was moved outside of useRecommendation because the dispatch did not sequentially update the LLM value
 */
let dataPrompt = {};
let sputnikArports = null;
function foundMathForSomeOriginDestinations(
  { origins: originsPrompts = [], destinations: destinationsPrompts = [] },
  originsIataCodeRouteTenants,
  destinationsIataCodeRouteTenants
) {
  let foundOrigin = false;
  let foundDestination = false;

  if (originsPrompts.length === 0 && destinationsPrompts.length === 0) {
    // The user did not specify any origin or destination, so it is considered an unrestricted query
    return true;
  } else {
    if (originsPrompts.length > 0 && destinationsPrompts.length > 0) {
      // Prompt Example: Flights from Miami to Antártida con un budget menor a 50 USD
      for (let index = 0; index < originsPrompts.length && !foundOrigin; index++) {
        if (originsIataCodeRouteTenants.includes(originsPrompts[index])) {
          foundOrigin = true;
        }
      }

      for (let index = 0; index < destinationsPrompts.length && !foundDestination; index++) {
        if (destinationsIataCodeRouteTenants.includes(destinationsPrompts[index])) {
          foundDestination = true;
        }
      }
      return foundOrigin && foundDestination;
    } else {
      // The LLM was able to extract some origin(s) from the user's prompt, so it checks if there is
      // an occurrence of them in the origins where the airline operates
      if (originsPrompts.length > 0) {
        for (let index = 0; index < originsPrompts.length && !foundOrigin; index++) {
          if (originsIataCodeRouteTenants.includes(originsPrompts[index])) {
            foundOrigin = true;
          }
        }
        return foundOrigin;
      } else {
        // The LLM was able to extract some destination(s) from the user's prompt, so it checks if there is
        // an occurrence of them in the origins where the airline operates
        if (destinationsPrompts.length > 0) {
          for (let index = 0; index < destinationsPrompts.length && !foundDestination; index++) {
            if (destinationsIataCodeRouteTenants.includes(destinationsPrompts[index])) {
              foundDestination = true;
            }
          }
          return foundDestination;
        }
      }
    }
  }
  return foundOrigin && foundDestination;
}

const filterDestination = ({ origin, destination }, origins, destinations) => {
  let filterModified = {};
  //INFO: Origins and destinations from the LLM are now filtered based on those coming from the airline
  if (Array.isArray(origins) && origins.length > 0) {
    filterModified.originsFiltered = origins
      .map((originIataCode) => origin.data.find((item) => item.iataCode === originIataCode))
      .filter(Boolean)
      .map((item) => item.iataCode);
  }

  if (Array.isArray(destinations) && destinations.length > 0) {
    filterModified.destinationsFiltered = destinations
      .map((destinationIataCode) =>
        destination.data.find((item) => item.iataCode === destinationIataCode)
      )
      .filter(Boolean)
      .map((item) => item.iataCode);
  }

  return filterModified;
};

export function useRecommendation({
  prompt,
  dateRange,
  sorting,
  prePopulation,
  pagination: { step, limit, initialVisibleItems } = {},
  meta,
  integrationSettings,
  getFares,
  getRecommendations,
  locations,
  fetchData,
  setFetchData,
  onLoadMore,
  setLoadMoreClick,
  pageContext,
  trackingDispatch,
  credits,
  fetchCredits
}) {
  let initialState = states.INIT;
  const [{ value: state, data, history }, dispatch] = useReducer(reducer, {
    value: initialState,
    data: [],
    dataPrompt: {},
    history: undefined
  });

  function match(stateValue) {
    return stateValue === state || (state === states.LOADING && history === stateValue);
  }

  const [index, setIndex] = useState(initialVisibleItems);
  const [fetchSecondQuery, setFetchSecondQuery] = useState(false);
  const overrideSecondQueryRef = useRef({});

  const hasMore = () => {
    if (Array.isArray(data)) {
      const itemCount = data.reduce((acc, item) => {
        const fareCount = item.fares.length;
        item.faresCount = fareCount;
        return acc + fareCount;
      }, 0);

      return itemCount < limit && itemCount >= index;
    }
    return false;
  };

  const loadMore = (_, withKeyboard) => {
    if (hasMore()) {
      setLoadMoreClick(true);
      // TODO: In some configuration cases, the current step + index may exceed the limit set by settings.
      //       In that case, check if the query cap is greater than the limit, then overwrite the newIndex with the limit.
      const newIndex = step + index > limit ? limit : step + index;
      getFaresData({
        paginationIndex: newIndex,
        paramsOverride: overrideSecondQueryRef.current,
        overrideFetchData: true,
        maintainSecondQuery: true
      });
      if (onLoadMore) {
        onLoadMore({ oldIndex: index, newIndex, withKeyboard });
      }
    }
  };
  const originsIataCodeRouteTenants = useMemo(() => {
    return locations.origin.data.map((item) => item.iataCode);
  }, [locations.origin.data]);

  const destinationsIataCodeRouteTenants = useMemo(() => {
    return locations.destination.data.map((item) => item.iataCode);
  }, [locations.destination.data]);

  const getData = () => {
    if (transitions[state].loading) {
      dispatch({ transition: 'loading' });
    }
    if (fetchData) {
      setFetchData(false);
      return getRecommendations(
        {
          tenantCode: prePopulation.tenantCode,
          moduleId: prePopulation.moduleId,
          prompt: prompt,
          sessionId: credits.session
        },

        integrationSettings,
        meta,
        pageContext
      )
        .then(({ promptValid, counter, done, ..._data }) => {
          if (!promptValid) {
            dispatch({ transition: 'prompt_invalid' });
          } else {
            dataPrompt = _data;
            dispatch({ transition: 'fetch_fares' }); // Changing state: FETCH_FARES
            getFaresData({ paramsOverride: _data }); // Call getFaresData after getRecommendation
          }
          setFetchSecondQuery(false);
        })
        .catch((err) => {
          dispatch({ transition: 'error', payload: { data: { success: false } } });
          console.error(err);
          throw err;
        })
        .finally(() => {
          fetchCredits();
        });
    }
  };

  const getFaresData = useCallback(
    ({
      paginationIndex = initialVisibleItems,
      overrideFetchData = false,
      paramsOverride,
      maintainSecondQuery = false
    }) => {
      if (transitions[state].fetch_fares) {
        dispatch({ transition: 'fetch_fares' });
      }

      function queryNotMatch() {
        return getFares(
          mapFilter({
            prompt,
            dateRange: computeDateRange({ start: 0, end: 90 }),
            sorting,
            prePopulation: {
              ...prePopulation,
              ...paramsOverride,
              ...overrideSecondQueryRef.current
            },
            locations,
            paginationIndex
          }),
          integrationSettings,
          meta,
          pageContext
        ).then((_dataSecond) => {
          const itemCountSecond = getResultsLength(_dataSecond);
          trackingDispatch({ type: 'NO_MATCH', payload: { prompt } });
          if (itemCountSecond === 0) {
            setFetchSecondQuery(false);
            dispatch({ transition: 'show_no_results', payload: { data: _dataSecond } });
          } else {
            setFetchSecondQuery(true);
            dispatch({
              transition: 'success',
              payload: { data: transformData(_dataSecond, dataPrompt) }
            });
          }
          setIndex(paginationIndex);
        });
      }

      if (fetchData || overrideFetchData) {
        const { originsFiltered, destinationsFiltered } = filterDestination(
          locations,
          dataPrompt?.origins,
          dataPrompt?.destinations
        );

        // The LLM returned origins and destinations, and after filtering,
        // you ended up with no origins or no destinations, so proceed directly with the notFares query
        if (
          (dataPrompt.origins?.length > 0 && originsFiltered.length === 0) ||
          (dataPrompt?.destinations?.length > 0 && destinationsFiltered.length === 0)
        ) {
          // Perform an open query by removing all parameters
          setFetchSecondQuery(true);
          overrideSecondQueryRef.current = {
            // origins: [],  Always keep the origin for the route
            destinations: [],
            return: undefined,
            passengers: undefined,
            budget: {},
            departure: undefined,
            flightStopCount: undefined,
            journeyType: undefined,
            travelClasses: '',
            tripDuration: '',
            weekDays: undefined
          };
          return queryNotMatch();
        } else {
          return getFares(
            mapFilter({
              prompt,
              dateRange,
              sorting,
              prePopulation: {
                ...prePopulation,
                ...dataPrompt,
                ...paramsOverride
              },
              locations,
              paginationIndex
            }),
            integrationSettings,
            meta,
            pageContext
          )
            .then((_data) => {
              /**
            When a user travels to a destination that is not on the airline's route, the use case is shown where it
            does not explain to the user that fares could not be found for that query
           */
              if (
                foundMathForSomeOriginDestinations(
                  dataPrompt,
                  originsIataCodeRouteTenants,
                  destinationsIataCodeRouteTenants
                )
              ) {
                const itemCount = getResultsLength(_data);
                if (itemCount === 0) {
                  // Execute getFares again with others params
                  // setFetchSecondQuery(true);
                  overrideSecondQueryRef.current = {
                    // origins: [],  Always keep the origin for the route
                    destinations: [],
                    return: undefined,
                    passengers: undefined,
                    budget: {},
                    departure: undefined,
                    flightStopCount: undefined,
                    journeyType: undefined,
                    travelClasses: '',
                    tripDuration: '',
                    weekDays: undefined
                  };
                  return queryNotMatch();
                } else {
                  if (!maintainSecondQuery) {
                    setFetchSecondQuery(false);
                  }
                  dispatch({
                    transition: 'success',
                    payload: { data: transformData(_data, dataPrompt) }
                  });
                }
              } else {
                setFetchSecondQuery(true);
                const itemCount = getResultsLength(_data);
                if (itemCount === 0) {
                  // Execute getFares again with others params
                  // setFetchSecondQuery(true);
                  overrideSecondQueryRef.current = {
                    // origins: [],  Always keep the origin for the route
                    destinations: [],
                    return: undefined,
                    passengers: undefined,
                    budget: {},
                    departure: undefined,
                    flightStopCount: undefined,
                    journeyType: undefined,
                    travelClasses: '',
                    tripDuration: '',
                    weekDays: undefined
                  };
                  return queryNotMatch();
                } else {
                  // setFetchSecondQuery(false);
                  dispatch({
                    transition: 'success',
                    payload: { data: transformData(_data, dataPrompt) }
                  });
                }
              }
              setIndex(paginationIndex);
            })
            .catch((err) => {
              dispatch({ transition: 'error', payload: { data: { success: false } } });
              console.error(err);
              throw err;
            });
        }
      }
    },
    [
      destinationsIataCodeRouteTenants,
      originsIataCodeRouteTenants,
      initialVisibleItems,
      trackingDispatch,
      getFares,
      fetchData,
      state,
      prompt,
      dateRange,
      sorting,
      prePopulation,
      locations,
      integrationSettings,
      meta,
      pageContext
    ]
  );

  const previousSortingRef = useRef();

  useEffect(() => {
    if (previousSortingRef.current !== undefined && previousSortingRef.current !== sorting) {
      getFaresData({ overrideFetchData: true });
    }
    previousSortingRef.current = sorting;
  }, [sorting, getFaresData]);

  useQuery({ getData, fetchData });

  return {
    dataPrompt,
    data,
    showSecondQuery: fetchSecondQuery,
    loadMore,
    hasMore,
    match,
    sputnikArports,
    retry: () => {
      setFetchData(true);
      getData();
    }
  };
}
